/**
 * EVO — savdo ma'lumot qatlami: buyurtma, narx, yuk xati, hisob-faktura.
 *
 * Tiplar `apps/api/src/modules/sales/*.service.ts` dagi javob
 * interfeyslaridan MAYDONMA-MAYDON ko'chirilgan (`lib/reports.ts` naqshi).
 *
 * ============================================================================
 * `margin?` — RUXSAT YO'Q BO'LSA BUTUN SHOX KELMAYDI
 * ============================================================================
 * Server marja maydonlarini bitta kalit ostiga yig'gan (`margin`), chunki
 * `field-masking.ts` butun shoxni olib tashlaydi. Yassi maydonlar bo'lsa
 * har biri alohida ro'yxatga qo'shilishi kerak bo'lardi va bitta unutilgan
 * nom = tannarx sizishi. UI ham xuddi shu shaklni saqlaydi: `margin` bormi
 * degan bitta tekshiruv butun blokni hal qiladi.
 *
 * ============================================================================
 * PUL — `string`. HISOB-KITOB SERVERDA.
 * ============================================================================
 * Bitta ONGLI istisno: buyurtma HALI SAQLANMAGAN paytdagi satr summasi va
 * jami (`/sales/orders/new`). Sabab va chegaralar `lib/decimal.ts` boshida.
 */
import { api, ApiError, isRecord } from './api';
import type { Page, SortDir } from './products';

// ---------------------------------------------------------------------------
// Sanaladigan qiymatlar (DB sxemasi bilan bir xil ro'yxatlar)
// ---------------------------------------------------------------------------

export const SALES_ORDER_STATUS = [
  'draft',
  'submitted',
  'confirmed',
  'partially_delivered',
  'delivered',
  'invoiced',
  'closed',
  'cancelled',
] as const;
export type SalesOrderStatus = (typeof SALES_ORDER_STATUS)[number];

/**
 * To'lov usuli — NARX TANLASHNING BIR QISMI, filtr emas.
 *
 * GAP_ANALYSIS §A5: O'zbekiston ulgurjisida naqd va bank o'tkazmasi narxi
 * 3-7% farq qiladi. Buyurtma naqd narxida yaratilib keyin bank orqali
 * to'lansa — o'sha 3-7% marja JIMGINA yo'qoladi.
 */
export const PAYMENT_METHODS = ['cash', 'transfer', 'mixed'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** `stock` — ombordagi tovar, `preorder` — hali yo'ldagi konteynerdan (§A1). */
export const FULFILLMENT_TYPES = ['stock', 'preorder'] as const;
export type FulfillmentType = (typeof FULFILLMENT_TYPES)[number];

export const PRICE_SOURCES = ['customer_price', 'price_list', 'manual'] as const;
export type PriceSource = (typeof PRICE_SOURCES)[number];

export const CURRENCIES = ['USD', 'CNY', 'UZS'] as const;
export type CurrencyCode = (typeof CURRENCIES)[number];

/** `passed` | `blocked` | `overridden` — `overridden` ekranda ALOHIDA ko'rsatiladi. */
export type CreditCheckResult = 'passed' | 'blocked' | 'overridden';

// ---------------------------------------------------------------------------
// Narx tanlash — GET /api/pricing/resolve
// ---------------------------------------------------------------------------

/**
 * Mijoz kartochkasidagi shartnomaviy chegirma — server `PricingService` bilan
 * BITTA javobda qaytaradi (`discount.support.ts` → `ContractDiscount`).
 *
 * ┌────────────────────────────────────────────────────────────────────────┐
 * │ NEGA U NARX BILAN BIRGA KELADI                                         │
 * │                                                                        │
 * │ Egasining talabi: «mijozga sotilganda narxlar o'zi kirib, chegirma     │
 * │ o'zi hisoblansin». Ya'ni ekran uchun narx va chegirma AJRALMAS: bittasi│
 * │ bo'lib ikkinchisi bo'lmasa, ko'rsatilgan summa hujjatdagidan farq      │
 * │ qiladi. Shuning uchun bu yerda ham ALOHIDA so'rov yo'q — maydon        │
 * │ serverda allaqachon bor edi, faqat bu tipda yozilmagandi va shu sabab  │
 * │ ekran uni ko'rmasdi.                                                   │
 * └────────────────────────────────────────────────────────────────────────┘
 */
export interface PriceContractDiscount {
  /** QO'LLANADIGAN foiz (`numeric(5,2)` matn). Kuchda bo'lmasa `'0.00'`. */
  readonly pct: string;
  readonly status: 'none' | 'active' | 'not_started' | 'expired' | 'superseded';
  /** Bloklamaydigan sabab: shartnoma tugagan, kelishilgan narx bosdi va h.k. */
  readonly warning: string | null;
}

export interface PriceResolution {
  /** Chegirmagacha bo'lgan narx. */
  readonly listPrice: string;
  readonly currencyCode: string;
  readonly priceSource: PriceSource;
  /** `customer_price` bo'lsa `null`. */
  readonly priceListId: number | null;
  readonly priceListCode: string | null;
  /** Qaysi hajm pog'onasi ishladi (`min_qty`). */
  readonly minQty: string | null;
  /**
   * Bloklamaydigan ogohlantirish. Eng muhimi: «ro'yxat to'lov usuliga mos
   * emas — naqd/o'tkazma farqi qo'llanmadi». Bu AYNAN §A5 dagi jimgina
   * yo'qotish, shuning uchun satr ostida ko'rsatiladi.
   */
  readonly warnings: readonly string[];
  /** Shartnomaviy chegirma. Mijozsiz so'rovda HAR DOIM `pct: '0.00'`, `status: 'none'`. */
  readonly contract: PriceContractDiscount;
}

export interface PriceResolveParams {
  /**
   * `null` — MIJOZSIZ narx (do'kon kassasi: mijoz OXIRIDA tanlanadi).
   *
   * Javobda `customer_prices` ham, shartnomaviy chegirma ham BO'LMAYDI —
   * serverdagi `resolveMany()` ramkasi. Ya'ni `null` bilan olingan narx
   * «prays narx» va u mijoz tanlangach QAYTA so'raladi (kesh kaliti
   * `salesKeys.price` mijozni ham qamraydi).
   */
  readonly customerId: number | null;
  readonly productId: number;
  /** MAJBURIY: narx hajm pog'onasiga bog'liq, miqdorsiz «narx» savolining javobi yo'q. */
  readonly quantity: string;
  readonly paymentMethod: PaymentMethod;
  readonly currencyCode: CurrencyCode;
  readonly date?: string | undefined;
}

/**
 * BITTA satrning narxi — `GET /api/pricing/resolve`.
 *
 * ┌────────────────────────────────────────────────────────────────────────┐
 * │ EKRANLAR BUNI EMAS, `resolvePriceBatched()` NI CHAQIRADI               │
 * │                                                                        │
 * │ Sabab `price-batch.ts` boshida: buyurtmada 10-100 satr bo'ladi va      │
 * │ ularni bittalab so'rash brauzerning 6 ta ulanish chegarasiga tiralib   │
 * │ qoladi. Bu funksiya endpointning to'g'ridan-to'g'ri ko'rinishi         │
 * │ sifatida qoladi — bitta satrni tekshirish va sinov uchun.              │
 * └────────────────────────────────────────────────────────────────────────┘
 */
export function resolvePrice(
  params: PriceResolveParams,
  signal?: AbortSignal,
): Promise<PriceResolution> {
  return api.get<PriceResolution>('/pricing/resolve', {
    query: {
      // `null` — maydon so'rovga UMUMAN qo'shilmaydi (`api.ts` `buildQuery`),
      // ya'ni server «mijozsiz» rejimga tushadi, `customerId=0` ni emas.
      customerId: params.customerId,
      productId: params.productId,
      quantity: params.quantity,
      paymentMethod: params.paymentMethod,
      currencyCode: params.currencyCode,
      date: params.date,
    },
    ...(signal ? { signal } : {}),
  });
}

/**
 * `422 PRICE_NOT_FOUND` — narx umuman topilmadi.
 *
 * Bu XATO EMAS, ma'lumot bo'shlig'i: mijozga ham, mahsulotga ham narx
 * berilmagan. Server nolga ham, tannarxga ham TUSHIB KETMAYDI (pricing.service.ts:
 * «narx topilmasa tannarxdan sotamiz» qoidasi marjani jimgina nolga tushiradi).
 *
 * Ekran uni satr ostida ko'rsatadi va sotuvchi qo'lda narx kiritishi mumkin.
 */
export function isPriceNotFound(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 422 &&
    isRecord(error.body) &&
    error.body.code === 'PRICE_NOT_FOUND'
  );
}

// ---------------------------------------------------------------------------
// Sotuv buyurtmasi
// ---------------------------------------------------------------------------

export interface SalesOrderLineMargin {
  readonly cogsBase: string;
  readonly grossProfit: string;
}

export interface SalesOrderLine {
  readonly id: number;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly uomCode: string | null;
  readonly quantity: string;
  readonly deliveredQuantity: string;
  readonly listPrice: string;
  readonly discountPct: string;
  readonly unitPrice: string;
  readonly lineAmount: string;
  readonly lineBase: string;
  readonly priceSource: string | null;
  readonly fulfillmentType: string;
  readonly shipmentId: number | null;
  readonly shipmentDocNo?: string | null;
  readonly promisedDate: string | null;
  readonly isBonus: boolean;
  readonly notes: string | null;
  /** FAQAT `so.margin.view` bilan keladi. */
  readonly margin?: SalesOrderLineMargin;
}

export interface SalesOrderMargin {
  readonly cogsBase: string;
  readonly grossProfit: string;
  readonly grossProfitPct: string;
  /**
   * Bonus dona tannarxi — DATA_MODEL §12A.3. Bu YASHIRIN CHEGIRMA: chegirma
   * tushumni kamaytiradi, bonus esa tannarxni oshiradi. Ikkalasi bitta
   * raqamga qo'shilsa «nega marja tushdi?» savoliga javob topilmaydi.
   */
  readonly bonusCostBase: string;
}

export interface SalesOrder {
  readonly id: number;
  readonly docNo: string;
  readonly customerId: number;
  readonly customerName: string;
  readonly legalEntityId: number;
  readonly branchId: number;
  readonly warehouseId: number;
  readonly salespersonId: number | null;
  readonly orderDate: string;
  readonly deliveryDate: string | null;
  readonly currencyCode: string;
  readonly fxRate: string;
  readonly subtotalAmount: string;
  readonly discountAmount: string;
  readonly totalAmount: string;
  readonly totalBase: string;
  readonly paymentTermDays: number;
  readonly paymentMethod: string;
  readonly status: string;
  readonly creditCheckResult: string | null;
  readonly creditOverrideBy: number | null;
  readonly notes: string | null;
  /** Bonus donalarning RO'YXAT narxidagi qiymati — bu narx, tannarx emas. */
  readonly bonusValue: string;
  readonly lines: readonly SalesOrderLine[];
  readonly warnings: readonly string[];
  /** FAQAT `so.margin.view` bilan keladi. */
  readonly margin?: SalesOrderMargin;
}

export interface SalesOrderListRow {
  readonly id: number;
  readonly docNo: string;
  readonly customerId: number;
  readonly customerName: string;
  readonly orderDate: string;
  readonly deliveryDate: string | null;
  readonly currencyCode: string;
  readonly totalAmount: string;
  readonly totalBase: string;
  readonly paymentMethod: string;
  readonly status: string;
  readonly creditCheckResult: string | null;
  readonly salespersonId: number | null;
  readonly warehouseId: number;
  /**
   * `erp` | `agent` | `portal` | `pos` — qaysi KANAL orqali keldi.
   *
   * `salespersonId` bilan adashtirmang: u KIM, bu esa QAYSI YO'LDAN
   * (sxemadagi `order_source` izohi). Yagona kanal ro'yxati mavjud emas —
   * ustun DB da erkin matn, shuning uchun web tomon noma'lum qiymatni
   * yorliqqa aylantirmasdan XOM ko'rsatadi.
   */
  readonly orderSource: string;
  /** Buyurtmadagi har xil tovar soni — qatorni ochmasdan bilinadigan hajm. */
  readonly lineCount: number;
}

export interface SalesOrderListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: SortDir;
  readonly q?: string | undefined;
  readonly status?: SalesOrderStatus | undefined;
  readonly customerId?: number | undefined;
  readonly warehouseId?: number | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  /** Faqat pre-order satri bor buyurtmalar — «yo'ldagi tovar» kesimi. */
  readonly hasPreorder?: boolean | undefined;
}

export function fetchSalesOrders(
  params: SalesOrderListParams,
  signal?: AbortSignal,
): Promise<Page<SalesOrderListRow>> {
  return api.get<Page<SalesOrderListRow>>('/sales-orders', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      status: params.status,
      customerId: params.customerId,
      warehouseId: params.warehouseId,
      from: params.from,
      to: params.to,
      // `BooleanFlag` — server MATN kutadi (`'false'` ni `false` deb o'qishi uchun).
      hasPreorder: params.hasPreorder === true ? 'true' : undefined,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchSalesOrder(id: number, signal?: AbortSignal): Promise<SalesOrder> {
  return api.get<SalesOrder>(`/sales-orders/${id}`, { ...(signal ? { signal } : {}) });
}

export interface SalesOrderLineInput {
  readonly productId: number;
  readonly quantity: string;
  /**
   * Qo'lda narx (`price_source = 'manual'`).
   *
   * `discountPct` bilan BIRGA yuborilmaydi — server 400 qaytaradi:
   * «qaysi biri yakuniy narx ekani noaniq bo'ladi».
   */
  readonly unitPrice?: string;
  readonly discountPct?: string;
  readonly fulfillmentType?: FulfillmentType;
  /** `preorder` uchun MAJBURIY. */
  readonly shipmentId?: number;
  readonly promisedDate?: string;
  /** Bonus satrga narx kiritilmaydi — u har doim 0 (server ham rad etadi). */
  readonly isBonus?: boolean;
  readonly notes?: string | null;
}

export interface CreateSalesOrderInput {
  readonly customerId: number;
  readonly warehouseId: number;
  readonly orderDate?: string;
  readonly deliveryDate?: string;
  readonly currencyCode?: CurrencyCode;
  readonly paymentMethod?: PaymentMethod;
  readonly paymentTermDays?: number;
  readonly notes?: string | null;
  readonly lines: readonly SalesOrderLineInput[];
}

export function createSalesOrder(input: CreateSalesOrderInput): Promise<SalesOrder> {
  return api.post<SalesOrder>('/sales-orders', input);
}

/**
 * Sarlavhani tahrirlash — faqat `draft` holatda.
 *
 * `paymentMethod` o'zgarsa server satrlarni QAYTA NARXLAYDI (`repriceLines`),
 * `manual` satrlarga esa TEGMAYDI — ular kelishuv natijasi.
 */
export interface UpdateSalesOrderInput {
  readonly deliveryDate?: string | null;
  readonly paymentMethod?: PaymentMethod;
  readonly paymentTermDays?: number;
  readonly notes?: string | null;
}

export function updateSalesOrder(id: number, input: UpdateSalesOrderInput): Promise<SalesOrder> {
  return api.patch<SalesOrder>(`/sales-orders/${id}`, input);
}

/**
 * Tasdiqlash — shu yerda kredit limiti tekshiriladi va rezerv yaratiladi.
 *
 * `creditOverride` ONGLI harakat bo'lishi kerak, shuning uchun sabab MAJBURIY
 * (GAP_ANALYSIS §B5: kim va NEGA qarzga ruxsat berganini ko'rsatadigan iz
 * xavfli tasdiqlar sonini kamaytiradi).
 */
export function confirmSalesOrder(
  id: number,
  override?: { readonly reason: string },
): Promise<SalesOrder> {
  return api.post<SalesOrder>(
    `/sales-orders/${id}/confirm`,
    override ? { creditOverride: true, overrideReason: override.reason } : { creditOverride: false },
  );
}

export function cancelSalesOrder(id: number, reason: string): Promise<SalesOrder> {
  return api.post<SalesOrder>(`/sales-orders/${id}/cancel`, { reason });
}

/** `422 CREDIT_LIMIT_EXCEEDED` — limit oshdi. Xabarda summalar bor, uni O'ZGARTIRMAYMIZ. */
export function isCreditLimitExceeded(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 422 &&
    isRecord(error.body) &&
    error.body.code === 'CREDIT_LIMIT_EXCEEDED'
  );
}

/** `422 DISCOUNT_*` — chegirma ruxsati yoki limiti. Xabar serverdan keladi. */
export function isDiscountRejected(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 422 || !isRecord(error.body)) return false;
  return error.body.code === 'DISCOUNT_LIMIT_EXCEEDED' || error.body.code === 'DISCOUNT_NOT_ALLOWED';
}

// ---------------------------------------------------------------------------
// Mijoz qarzi — GET /api/customers/:id/balance (ar.view)
// ---------------------------------------------------------------------------

export interface AgingBucket {
  readonly label: string;
  readonly amountBase: string;
  readonly invoiceCount: number;
}

export interface CustomerBalance {
  readonly customerId: number;
  readonly customerName: string;
  /** Qarz HAR DOIM USD da yuritiladi (DATA_MODEL §1.4). */
  readonly balanceBase: string;
  readonly overdueBase: string;
  readonly unallocatedPaymentsBase: string;
  readonly netBase: string;
  readonly creditLimitAmount: string;
  readonly creditLimitCurrency: string;
  readonly creditLimitBase: string;
  /** Yana qancha tovar qarzga berish mumkin. MANFIY = limit allaqachon oshgan. */
  readonly availableCreditBase: string;
  readonly fxDebtPolicy: string;
  readonly paymentTermDays: number;
  readonly isBlocked: boolean;
  readonly aging: readonly AgingBucket[];
  /**
   * Ochiq hisob-fakturalar — muddat bo'yicha, eng eskisidan.
   *
   * Bu maydon SERVERDA ALLAQACHON BOR EDI (`ar.service.ts` →
   * `CustomerBalanceResponse.openInvoices`), lekin bu tipda yo'q edi va
   * shuning uchun uni hech kim ishlatmasdi. Qo'shildi, chunki mijoz
   * qarzlari ekranining (`/receivables`) butun ma'nosi shu ro'yxatda:
   * «$13.95 qarz» degan raqam o'z-o'zicha ish emas, uning ORTIDAGI
   * hisob-faktura esa aniq harakat.
   *
   * Maydon MAJBURIY (`?:` emas): u ruxsatga bog'liq emas — endpointning
   * o'zi `ar.view` bilan yopilgan va javob har doim to'liq keladi.
   */
  readonly openInvoices: readonly OpenInvoiceRow[];
}

/** `ar.service.ts` → `OpenInvoiceRow`. Summalar hisob-faktura valyutasida. */
export interface OpenInvoiceRow {
  readonly id: number;
  readonly docNo: string;
  readonly invoiceDate: string;
  readonly dueDate: string;
  readonly currencyCode: string;
  readonly totalAmount: string;
  readonly paidAmount: string;
  readonly balanceAmount: string;
  /** MANFIY = muddat hali kelmagan. */
  readonly daysOverdue: number;
}

export function fetchCustomerBalance(
  customerId: number,
  signal?: AbortSignal,
): Promise<CustomerBalance> {
  return api.get<CustomerBalance>(`/customers/${customerId}/balance`, {
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// Konteyner (pre-order tanlagichi uchun) — GAP_ANALYSIS §A1
// ---------------------------------------------------------------------------

export interface ShipmentLine {
  readonly id: number;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly uomCode: string | null;
  readonly quantity: string;
  readonly reservedQuantity: string;
  /** «Bu konteynerdan qancha sotilgan» — keyingi xarid qarorining asosi. */
  readonly soldQuantity: string;
  /** `quantity − reservedQuantity` — hali sotilmagan, ERKIN qism. */
  readonly freeQuantity: string;
}

/**
 * Pre-order uchun konteyner sarlavhasi — `GET /api/shipments`.
 *
 * Manba `lib/procurement.ts` dagi `ShipmentHeader` bilan BIR XIL endpoint,
 * ya'ni uning UCH QATLAMLI proyeksiyasi bu yerda ham amal qiladi va
 * maskalanadigan maydonlar IXTIYORIY bo'lishi shart.
 *
 * Bu ayniqsa shu fayl uchun muhim: pre-order ekranining asosiy foydalanuvchisi
 * — SOTUVCHI, ya'ni aynan `shipment.edit` va `supplier.view` ruxsatlari YO'Q
 * rol. Jonli tekshirilgan: `sotuvchi@evo.uz` javobida `containerNo`, `etd`,
 * `ata`, `lcStatus` kalitlari umuman yo'q.
 *
 * `eta` va `status` ochiq qoladi va bu ataylab: sotuvchi mijozga muddat
 * aytishi hamda yopiq konteynerni ro'yxatdan chiqarib tashlashi kerak.
 */
export interface ShipmentHeader {
  readonly id: number;
  readonly docNo: string;
  /** Kutilayotgan kelish sanasi. Sotuvchi mijozga AYNAN shu sanani aytadi. */
  readonly eta: string | null;
  readonly status: string;

  /** --- `shipment.edit` qatlami --- */
  readonly containerNo?: string | null;
  readonly etd?: string | null;
  readonly ata?: string | null;
  readonly lcStatus?: string;

  /** FAQAT `supplier.view` bilan keladi (MATRIX §3.3 izoh⁷). */
  readonly supplierName?: string | null;
}

export interface Shipment extends ShipmentHeader {
  readonly lines: readonly ShipmentLine[];
}

/** Qabul qilingan/yopilgan konteynerdan pre-order qilib bo'lmaydi (server ham rad etadi). */
export function isOpenShipment(shipment: ShipmentHeader): boolean {
  return shipment.status !== 'received' && shipment.status !== 'closed';
}

/**
 * Ochiq konteynerlarni SATRLARI bilan yuklaydi.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN N TA SO'ROV                                               │
 * │                                                                      │
 * │ `GET /api/shipments` sarlavhalarni qaytaradi, ERKIN MIQDOR esa faqat │
 * │ `GET /api/shipments/:id` javobidagi satrlarda bor. Pre-order satrida │
 * │ sotuvchiga aynan «bu SKU dan qancha erkin» kerak — usiz u mijozga    │
 * │ ikki marta va'da berib qo'yadi.                                      │
 * │                                                                      │
 * │ Shuning uchun ochiq konteynerlar (odatda 10-30 ta) bir marta         │
 * │ yuklanadi va react-query keshida turadi. `LIMIT` bilan cheklangan:   │
 * │ ro'yxat kutilmaganda uzun bo'lsa ham ekran 50 ta so'rovdan oshmaydi. │
 * └──────────────────────────────────────────────────────────────────────┘
 */
const OPEN_SHIPMENT_LIMIT = 50;

export async function fetchOpenShipments(signal?: AbortSignal): Promise<readonly Shipment[]> {
  const page = await api.get<Page<ShipmentHeader>>('/shipments', {
    // ETA bo'yicha: eng yaqin keladigan konteyner ro'yxat boshida turadi.
    query: { pageSize: OPEN_SHIPMENT_LIMIT, sortBy: 'eta', sortDir: 'asc' },
    ...(signal ? { signal } : {}),
  });

  const open = page.items.filter(isOpenShipment);

  return Promise.all(
    open.map((header) =>
      api.get<Shipment>(`/shipments/${header.id}`, { ...(signal ? { signal } : {}) }),
    ),
  );
}

// ---------------------------------------------------------------------------
// Yuk xati (накладная)
// ---------------------------------------------------------------------------

export interface DeliveryListRow {
  readonly id: number;
  readonly docNo: string;
  readonly salesOrderId: number;
  readonly salesOrderNo: string;
  readonly warehouseId: number;
  readonly deliveryDate: string;
  readonly status: string;
  readonly driverName: string | null;
  readonly vehicleNo: string | null;
}

export interface Delivery extends DeliveryListRow {
  readonly customerId: number;
  readonly notes: string | null;
  readonly warnings: readonly string[];
}

export function fetchDeliveries(
  salesOrderId: number,
  signal?: AbortSignal,
): Promise<Page<DeliveryListRow>> {
  return api.get<Page<DeliveryListRow>>('/deliveries', {
    query: { salesOrderId, pageSize: 50 },
    ...(signal ? { signal } : {}),
  });
}

/**
 * Yuk xati yaratadi — LEKIN POST QILMAYDI.
 *
 * Ikki bosqich ataylab: hujjat chop etiladi, mashinaga yuklanadi va faqat
 * tovar haqiqatan chiqib ketgach post qilinadi (`delivery.post`, omborchida).
 * `lines` berilmasa — buyurtmaning qolgan BARCHA miqdori olinadi.
 */
export function createDelivery(salesOrderId: number): Promise<Delivery> {
  return api.post<Delivery>('/deliveries', { salesOrderId });
}

// ---------------------------------------------------------------------------
// Hisob-faktura
// ---------------------------------------------------------------------------

export interface InvoiceListRow {
  readonly id: number;
  readonly docNo: string;
  readonly customerId: number;
  readonly customerName: string;
  readonly invoiceDate: string;
  readonly dueDate: string;
  readonly currencyCode: string;
  readonly totalAmount: string;
  readonly paidAmount: string;
  readonly balanceAmount: string;
  readonly status: string;
  /** Manfiy = hali muddati kelmagan. */
  readonly daysOverdue: number;
}

export function fetchInvoices(
  salesOrderId: number,
  signal?: AbortSignal,
): Promise<Page<InvoiceListRow>> {
  return api.get<Page<InvoiceListRow>>('/invoices', {
    query: { salesOrderId, pageSize: 50 },
    ...(signal ? { signal } : {}),
  });
}

/**
 * Hisob-faktura FAKTDA chiqqan tovarga yoziladi.
 *
 * Satrlar so'rovda YO'Q: ular POST QILINGAN yuk xati satrlaridan quriladi.
 * Aks holda mijozga chiqmagan tovar uchun hisob yozish mumkin bo'lardi —
 * bu qarzni ham, tushum hisobotini ham soxta qiladi.
 */
export function createInvoice(salesOrderId: number): Promise<{ readonly id: number; readonly docNo: string }> {
  return api.post<{ id: number; docNo: string }>('/invoices', { salesOrderId });
}

// ---------------------------------------------------------------------------
// Query kalitlari
// ---------------------------------------------------------------------------

export const salesKeys = {
  all: ['sales'] as const,
  orders: (params: SalesOrderListParams) => ['sales', 'orders', params] as const,
  order: (id: number) => ['sales', 'order', id] as const,
  balance: (customerId: number) => ['sales', 'balance', customerId] as const,
  openShipments: ['sales', 'open-shipments'] as const,
  deliveries: (salesOrderId: number) => ['sales', 'deliveries', salesOrderId] as const,
  invoices: (salesOrderId: number) => ['sales', 'invoices', salesOrderId] as const,
  /**
   * Narx kaliti tarkibiga TO'LOV USULI ham kiradi.
   *
   * Aks holda naqddan o'tkazmaga o'tilganda react-query keshdan ESKI narxni
   * qaytaradi va ekranda «o'tkazma» yozuvi ostida naqd narxi turadi —
   * aynan §A5 dagi jimgina yo'qotish, faqat brauzer tomonda.
   */
  price: (params: PriceResolveParams) =>
    [
      'sales',
      'price',
      /*
       * Mijozsiz narx ALOHIDA kalitda turadi (`'no-customer'`), `0` da emas:
       * `0` bir kun kelib haqiqiy `id` bilan chalkashardi va mijoz tanlangach
       * kesh ESKI, chegirmasiz narxni qaytarardi — ekranda prays narx, hujjatda
       * chegirmali narx.
       */
      params.customerId ?? 'no-customer',
      params.productId,
      params.quantity,
      params.paymentMethod,
      params.currencyCode,
    ] as const,
};
